import { create } from 'zustand'
import { produce } from 'immer'
import type {
  AssetId,
  IndicationId,
  KiqId,
  Submission,
  DraftRecording,
  Settings,
} from '@/types'

// Selection state slice
interface SelectionState {
  selectedAssetId: AssetId | null
  selectedIndicationId: IndicationId | null
  selectedKiqIds: KiqId[]
  setAsset: (assetId: AssetId | null) => void
  setIndication: (indicationId: IndicationId | null) => void
  setKiqIds: (kiqIds: KiqId[]) => void
  toggleKiq: (kiqId: KiqId) => void
  selectedInsightTags: string[]
  setInsightTags: (tags: string[]) => void
  toggleInsightTag: (tag: string) => void
  clearSelection: () => void
}

// Recording state slice
interface RecordingState {
  isRecording: boolean
  isPaused: boolean
  durationMs: number
  currentDraft: DraftRecording | null
  setRecording: (isRecording: boolean) => void
  setPaused: (isPaused: boolean) => void
  setDuration: (durationMs: number) => void
  setDraft: (draft: DraftRecording | null) => void
  resetRecording: () => void
}

// Submissions queue slice
interface SubmissionsState {
  submissions: Submission[]
  isLoading: boolean
  error: string | null
  setSubmissions: (submissions: Submission[]) => void
  addSubmission: (submission: Submission) => void
  updateSubmission: (id: string, updates: Partial<Submission>) => void
  removeSubmission: (id: string) => void
  setLoading: (isLoading: boolean) => void
  setError: (error: string | null) => void
}

// Settings slice
interface SettingsState {
  settings: Settings
  updateSettings: (updates: Partial<Settings>) => void
  resetSettings: () => void
}

// Root store combining all slices
export interface RootState
  extends SelectionState,
    RecordingState,
    SubmissionsState,
    SettingsState {}

const defaultSettings: Settings = {
  installPromptShownAt: null,
  hasInstalled: false,
  dismissedToasts: [],
}

export const useStore = create<RootState>()((set) => ({
    // Selection state
    selectedAssetId: null,
    selectedIndicationId: null,
    selectedKiqIds: [],
    selectedInsightTags: [],
    setAsset: (assetId) =>
      set(
        produce((state: RootState) => {
          state.selectedAssetId = assetId
          // Clear indication and KIQs when asset changes
          if (state.selectedIndicationId) {
            state.selectedIndicationId = null
          }
          if (state.selectedKiqIds.length > 0) {
            state.selectedKiqIds = []
          }
        })
      ),
    setIndication: (indicationId) =>
      set(
        produce((state: RootState) => {
          state.selectedIndicationId = indicationId
          // Clear KIQs when indication changes
          if (state.selectedKiqIds.length > 0) {
            state.selectedKiqIds = []
          }
        })
      ),
    setKiqIds: (kiqIds) =>
      set(
        produce((state: RootState) => {
          state.selectedKiqIds = kiqIds
        })
      ),
    toggleKiq: (kiqId) =>
      set(
        produce((state: RootState) => {
          const index = state.selectedKiqIds.indexOf(kiqId)
          if (index === -1) {
            state.selectedKiqIds.push(kiqId)
          } else {
            state.selectedKiqIds.splice(index, 1)
          }
        })
      ),
    // Temporary, binder-gated "Insight Type" tags — independent from KIQs, so
    // NOT cleared when asset/indication changes (only on clearSelection).
    setInsightTags: (tags) =>
      set(
        produce((state: RootState) => {
          state.selectedInsightTags = tags
        })
      ),
    toggleInsightTag: (tag) =>
      set(
        produce((state: RootState) => {
          const index = state.selectedInsightTags.indexOf(tag)
          if (index === -1) {
            state.selectedInsightTags.push(tag)
          } else {
            state.selectedInsightTags.splice(index, 1)
          }
        })
      ),
    clearSelection: () =>
      set(
        produce((state: RootState) => {
          state.selectedAssetId = null
          state.selectedIndicationId = null
          state.selectedKiqIds = []
          state.selectedInsightTags = []
        })
      ),

    // Recording state
    isRecording: false,
    isPaused: false,
    durationMs: 0,
    currentDraft: null,
    setRecording: (isRecording) =>
      set(
        produce((state: RootState) => {
          state.isRecording = isRecording
        })
      ),
    setPaused: (isPaused) =>
      set(
        produce((state: RootState) => {
          state.isPaused = isPaused
        })
      ),
    setDuration: (durationMs) =>
      set(
        produce((state: RootState) => {
          state.durationMs = durationMs
        })
      ),
    setDraft: (draft) =>
      set(
        produce((state: RootState) => {
          state.currentDraft = draft
        })
      ),
    resetRecording: () =>
      set(
        produce((state: RootState) => {
          state.isRecording = false
          state.isPaused = false
          state.durationMs = 0
          state.currentDraft = null
        })
      ),

    // Submissions state
    submissions: [],
    isLoading: false,
    error: null,
    setSubmissions: (submissions) =>
      set(
        produce((state: RootState) => {
          state.submissions = submissions
        })
      ),
    addSubmission: (submission) =>
      set(
        produce((state: RootState) => {
          state.submissions.unshift(submission) // Add to beginning
        })
      ),
    updateSubmission: (id, updates) =>
      set(
        produce((state: RootState) => {
          const index = state.submissions.findIndex((s) => s.id === id)
          if (index !== -1) {
            Object.assign(state.submissions[index], updates)
          }
        })
      ),
    removeSubmission: (id) =>
      set(
        produce((state: RootState) => {
          state.submissions = state.submissions.filter((s) => s.id !== id)
        })
      ),
    setLoading: (isLoading) =>
      set(
        produce((state: RootState) => {
          state.isLoading = isLoading
        })
      ),
    setError: (error) =>
      set(
        produce((state: RootState) => {
          state.error = error
        })
      ),

    // Settings state
    settings: defaultSettings,
    updateSettings: (updates) =>
      set(
        produce((state: RootState) => {
          Object.assign(state.settings, updates)
        })
      ),
    resetSettings: () =>
      set(
        produce((state: RootState) => {
          state.settings = { ...defaultSettings }
        })
      ),
  }))

